import React, { forwardRef } from 'react';
import { PhoneInput, type PhoneInputRefType } from 'react-international-phone';
import 'react-international-phone/style.css';
import FormField from './FormField';

export interface InputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  fieldType?: 'text' | 'email' | 'phone' | 'password' | 'number' | 'url';
  // For phone input
  phoneValue?: string;
  onPhoneChange?: (phone: string) => void;
  phoneCountry?: string;
}

/**
 * Reusable input field component supporting multiple input types
 * Includes text, email, phone, password, number, and URL inputs
 */
const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      fieldType = 'text',
      className = '',
      phoneValue,
      onPhoneChange,
      phoneCountry = 'us',
      ...props
    },
    ref
  ) => {
    // Render phone input separately
    if (fieldType === 'phone') {
      const { disabled } = props;
      return (
        <FormField
          label={label}
          description={description}
          error={error}
          required={required}
        >
          <PhoneInput
            // PhoneInput's ref is the underlying <input>, so react-hook-form can focus/scroll to it
            ref={ref as React.Ref<PhoneInputRefType>}
            defaultCountry={phoneCountry}
            value={phoneValue || ''}
            onChange={onPhoneChange || (() => {})}
            inputClassName="w-full"
            disabled={disabled}
          />
        </FormField>
      );
    }

    // Render standard input
    return (
      <FormField
        label={label}
        description={description}
        error={error}
        required={required}
      >
        <input
          ref={ref}
          type={fieldType}
          autoComplete="off"
          className={`w-full input-field ${error ? 'border-red-500 focus:ring-red-500' : ''} ${className}`}
          {...props}
        />
      </FormField>
    );
  }
);

InputField.displayName = 'InputField';

export default InputField;
